import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import {
  LicensesActionsTypes,
  getActiveLicensesInfoSuccessAction,
  getActiveLicensesInfoFailedAction,
  getExpiredLicensesInfoSuccessAction,
  getExpiredLicensesInfoFailedAction,
  createLicensesOrderSuccessAction,
  createLicensesOrderFailedAction,
  validateLicensesSuccessAction,
  validateLicensesFailedAction,
  getLicensesLocalStorageSuccessAction,
  addLicenseToShoppingBasketSuccessAction,
  removeLicenseFromShoppingBasketSuccessAction,
  removeLicenseFromShoppingBasketFailedAction,
  updateLicensesShoppingBasketAction,
  updateLicensesShoppingBasketSuccessAction,
  updateLicensesShoppingBasketFailedAction,
  clearShoppingBasketSuccessAction,
  clearShoppingBasketFailedAction,
  addLicenseToShoppingBasketFailedAction,
  getAuthInfoLicensesSuccessAction,
  getAuthInfoLicensesFailedAction,
  getExpiringLicensesInfoSuccessAction,
  getExpiringLicensesInfoFailedAction,
  validateLicensesAction,
  getOrdersSuccessAction,
  getOrdersFailedAction,
  getOrderSuccessAction,
  getOrderFailedAction,
  setSubscriptionSuccessAction,
  setSubscriptionFailedAction,
  getSubscriptionFailedAction,
  getSubscriptionSuccessAction,
  getLicensesDetailsInfoSuccessAction,
  getLicensesDetailsInfoFailedAction,
  getInsightsOffersSuccessAction,
  getInsightsOffersFailedAction,
  createInsightsOrderSuccessAction,
  createInsightsOrderFailedAction,
} from '../actions/licenses.actions';
import { catchError, exhaustMap, filter, map, take } from 'rxjs/operators';
import { AnnotatedOrder, AuthenticationService, DealerRemoteMaintenanceOrder, DealerRemoteMaintenanceValidateOrderItem, DeviceSubscriptionOfferExt, InsightsLicensesService, InsightsOffer, InsightsTierOrder, ManualSubscription, OrderItemStatus, PermissionsApi, RemoteMaintenanceLicensesService, SubscriptionDetails } from 'src/app/generated/CrossPoint.License.Server';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { selectShoppingBasket } from '../selectors/licenses.selectors';
import { LOCAL_STORAGE_SHOPPING_BASKET_KEY } from 'src/app/utils/constants/licenses.consts';
import { HttpErrorResponse } from '@angular/common/http';
import { errorEventAction } from '../actions/app.actions';
import { IDataAction } from 'src/app/utils/interfaces/common.interfaces';
import { ICreateInsightsOrder, IOrdedLicenses } from 'src/app/utils/interfaces/remote-maintenance.interfaces';

@Injectable()
export class LicensesEffects {
  public addLicenseToShoppingBasketEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.ADD_LICENSE_SHOPPING_BASKET),
        map(({ data: offer }) => addLicenseToShoppingBasketSuccessAction({ data: offer })),
        catchError((error: HttpErrorResponse) => {
          return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.AddLicenseToShoppingBasketFailed" } }), addLicenseToShoppingBasketFailedAction())
        })
      )
  );

  public clearShoppingBasketEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.CLEAR_SHOPPING_BASKET),
        map(() => clearShoppingBasketSuccessAction()),
        catchError((error: HttpErrorResponse) => {
          return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.ClearShoppingBasketFailed" } }), clearShoppingBasketFailedAction())
        })
      )
  );

  public createLicensesOrderEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.CREATE_LICENSE_ORDER),
        exhaustMap(({ data: { dealerId, copyEmail, deviceSubscriptionOffer } }) =>
          this.licensesService.createOrder(dealerId, copyEmail, deviceSubscriptionOffer)
            .pipe(
              map((order: DealerRemoteMaintenanceOrder) => createLicensesOrderSuccessAction({ data: order })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateLicensesOrderFailed" } }), createLicensesOrderFailedAction())
              })
            )
        )
      )
  );


  public getLicensesDetailsInfoEffect$: Observable<Action> = createEffect(() =>
  this.actions$
    .pipe(
      ofType(LicensesActionsTypes.GET_LICENSES_DETAILS_INFO),
      exhaustMap(({ data }) => this.licensesService.getSubscriptionDetails(data)
        .pipe(
          map((licensesDetails: SubscriptionDetails) => getLicensesDetailsInfoSuccessAction({ data: licensesDetails })),
          catchError((error: HttpErrorResponse) => {
            return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceConnectionsFailed" } }), getLicensesDetailsInfoFailedAction())
          })
        )
      )
    )
);

  public getActiveLicensesInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_ACTIVE_LICENSES_INFO),
        exhaustMap(({ data: {subscriptionState, dealerId, search, maxItems} }) => {          
          return this.licensesService.getSubscriptionInfo(subscriptionState, dealerId, maxItems, search)
            .pipe(
              map((activeStoreLicensesInfo: DeviceSubscriptionOfferExt[]) => getActiveLicensesInfoSuccessAction({ data: activeStoreLicensesInfo })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetActiveLicensesInfoFailed" } }), getActiveLicensesInfoFailedAction())
              })
            );
        })
      )
  );

  public getExpiringLicensesInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_EXPIRING_LICENSES_INFO),
        exhaustMap(({ data: {subscriptionState, dealerId, search, maxItems} }) => {          
          return this.licensesService.getSubscriptionInfo(subscriptionState, dealerId, maxItems, search)
            .pipe(
              map((activeStoreLicensesInfo: DeviceSubscriptionOfferExt[]) => getExpiringLicensesInfoSuccessAction({ data: activeStoreLicensesInfo })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetActiveLicensesInfoFailed" } }), getExpiringLicensesInfoFailedAction())
              })
            );
        })
      )
  );

  public getExpiredLicensesInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_EXPIRED_LICENSES_INFO),
        exhaustMap(({ data: {subscriptionState, dealerId, search, maxItems} }) => {          
          return this.licensesService.getSubscriptionInfo(subscriptionState, dealerId, maxItems, search)
            .pipe(
              map((expiredStoreLicensesInfo: DeviceSubscriptionOfferExt[]) => getExpiredLicensesInfoSuccessAction({ data: expiredStoreLicensesInfo })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetExpiredLicensesInfoFailed" } }), getExpiredLicensesInfoFailedAction())
              })
            );
        })
      )
  );

  public getLocalStorageLicensesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_LOCAL_STORAGE_SHOPPING_BASKET),
        map(() => {
          const cachedShoppingBasket: string | undefined = localStorage[LOCAL_STORAGE_SHOPPING_BASKET_KEY];

          if (!cachedShoppingBasket) {
            // Nothing in local storage
            return getLicensesLocalStorageSuccessAction({ data: [] });
          }

          const rawShoppingBasket: DeviceSubscriptionOfferExt[] = JSON.parse(cachedShoppingBasket!);
          let shoppingBasket: DeviceSubscriptionOfferExt[] = [];

          // Do not accept duplicate items in the shopping basket
          for (let offer of rawShoppingBasket) {
            const isAlreadyAdded: boolean = shoppingBasket.some((o: DeviceSubscriptionOfferExt) => o.serial === offer.serial);

            if (!isAlreadyAdded) {
              if(offer.months === 0){
                offer.basePrice = 0;
              }

              shoppingBasket = [...shoppingBasket, offer];
            }
          }

          this.store.dispatch(updateLicensesShoppingBasketAction({ data: shoppingBasket }));
          this.store.dispatch(validateLicensesAction({ data: { licenses: shoppingBasket } }));
          return getLicensesLocalStorageSuccessAction({ data: shoppingBasket });
        })
      )
  );

  public removeLicenseFromShoppingBasketEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.REMOVE_LICENSE_SHOPPING_BASKET),
        map(({ data: offer }) => {
          return removeLicenseFromShoppingBasketSuccessAction({ data: offer });
        }),
        catchError((error: HttpErrorResponse) => {
          return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.RemoveLicenseFromShoppingBasketFailed" } }), removeLicenseFromShoppingBasketFailedAction())
        })
      )
  );

  public updateLicensesShoppingBasketEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.UPDATE_LICENSE_SHOPPING_BASKET),
        map(({ data: updatedShoppingBasket }) => {
          return updateLicensesShoppingBasketSuccessAction({ data: updatedShoppingBasket });
        }),
        catchError((error: HttpErrorResponse) => {
          return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UpdateLicensesShoppingBasketFailed" } }), updateLicensesShoppingBasketFailedAction())
        })
      )
  );

  public validateLicensesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.VALIDATE_LICENSES),
        exhaustMap(({ data: { dealerId, licenses } }) => {

          return this.licensesService.validateOrder(dealerId, licenses)
            .pipe(
              map((items: any[]) => {
                this.store.select(selectShoppingBasket)
                  .pipe(
                    filter<DeviceSubscriptionOfferExt[]>(Boolean),
                    take(1)
                  )
                  .subscribe((shoppingBasket: DeviceSubscriptionOfferExt[]) => {
                    let validatedShoppingBasket: DeviceSubscriptionOfferExt[] = [];

                    for (const offer of shoppingBasket) {
                      const validationItem: DealerRemoteMaintenanceValidateOrderItem | undefined = items.find((item: DealerRemoteMaintenanceValidateOrderItem) => item.serial === offer.serial);

                      if (validationItem === undefined) {
                        continue;
                      }

                      if (validationItem.status === OrderItemStatus.NewSubscription || validationItem.status === OrderItemStatus.ReactivatedSubscription
                        || validationItem.status === OrderItemStatus.CancelledSubscription) {
                        validatedShoppingBasket = [...validatedShoppingBasket, offer];
                      }                      
                    }

                    this.store.dispatch(updateLicensesShoppingBasketAction({ data: validatedShoppingBasket }));
                  });

                return validateLicensesSuccessAction({ data: items })
              }),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.ValidateLicensesFailed" } }), validateLicensesFailedAction())
              })
            );
        })
      )
  );

  public getOrdedLicensesInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_ORDERS),
        exhaustMap(({ data: { startUtc, endUtc, searchText, maxItems } }: IDataAction<IOrdedLicenses>) => {
          return this.licensesService.getOrders(startUtc, endUtc, searchText, maxItems)
            .pipe(
              map((activeStoreLicensesInfo: DealerRemoteMaintenanceOrder[]) => getOrdersSuccessAction({ data: activeStoreLicensesInfo })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetOrdedLicensesInfoFailed" } }), getOrdersFailedAction())
              })
            );
        })
      )
  );

  public getOrderLicensesInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_ORDER),
        exhaustMap(({data}) => this.licensesService.getOrder(data)
          .pipe(
            map((orderLicenseDetails: AnnotatedOrder) => getOrderSuccessAction({data: orderLicenseDetails})),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({data: {error: error, translationkey: "StoreActionFailed.GetOrderLicensesInfoFailed" }}), getOrderFailedAction())
            })
          )
        )
      )
  );

  public getSubscriptionEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_SUBSCRIPTION),
        exhaustMap(({data: deviceId}) => this.licensesService.getSubscription(deviceId)
          .pipe(
            map((subscription: ManualSubscription) => getSubscriptionSuccessAction({ data: { deviceId, subscription } })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({data: {error: error, translationkey: "StoreActionFailed.GetSubscriptionFailed" }}), getSubscriptionFailedAction())
            })
          )
        )
      )
  );

  public setSubscriptionEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.SET_SUBSCRIPTION),
        exhaustMap(({data: { serial, endDate, flags }}) => this.licensesService.setSubscription(serial, {endDate, flags})
          .pipe(
            map(() => setSubscriptionSuccessAction()),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({data: {error: error, translationkey: "StoreActionFailed.SetSubscriptionFailed" }}), setSubscriptionFailedAction())
            })
          )
        )
      )
  );

  public getAuthInfoDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_AUTH_INFO_LICENSES),
        exhaustMap(() => this.authenticationService.getPermissions()
          .pipe(
            map((permissions: PermissionsApi) => getAuthInfoLicensesSuccessAction({ data: permissions.permissions })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoLicensesErrorHasOccurred" } }), getAuthInfoLicensesFailedAction())
            })
          )
        )
      )
  );

  public getInsightsOffersEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.GET_INSIGHTS_OFFERS),
        exhaustMap(({ data: retailerId }) => this.insightsService.getInsightsOffers(retailerId)
          .pipe(
            map((offers: InsightsOffer[]) => getInsightsOffersSuccessAction({ data: offers })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetInsightsOffersFailed" } }), getInsightsOffersFailedAction({ data: retailerId }))
            })
          )
        )
      )
  );

  public createInsightsOrderEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(LicensesActionsTypes.CREATE_INSIGHTS_ORDER),
        exhaustMap(({ data }: IDataAction<ICreateInsightsOrder>) => this.insightsService.createInsightsOrder(data.retailerId, data.tier)
          .pipe(
            map((order: InsightsTierOrder) => createInsightsOrderSuccessAction({ data: order })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateInsightsOrderFailed" } }), createInsightsOrderFailedAction({ data: data }))
            })
          )
        )
      )
  );

  constructor(
    private actions$: Actions, 
    private licensesService: RemoteMaintenanceLicensesService,
    private insightsService: InsightsLicensesService,
    private store: Store<IRemoteXPACPortalState>,
    private authenticationService: AuthenticationService
  ) { }
}
